/**
 * <StudentCorrectionPage> — entre la copie rendue et l'écran de fin : Sam corrige (page).
 *
 * L'ancienne `CorrectionSequence`, quand le serveur a dit à l'envoi que Sam corrigera la copie.
 * Une étape à la fois (`step`, contrôlée) :
 *  - `intro` : « Sam corrige ta copie », seul à l'écran, le temps de le lire ;
 *  - `waiting` : Sam se range en haut et laisse la place à de quoi patienter (`game` : le « Défi
 *    éclair » ou une pause) ; « Continuer sans attendre » en pied ;
 *  - `ready` : « Ta correction est prête ! », un instant, avant qu'elle ne s'ouvre ;
 *  - `ending` : l'attente prend fin sans correction (`endingText` : déjà beaucoup de copies
 *    aujourd'hui, échec, délai dépassé) ; « Activité suivante » ;
 *  - `copy` : la copie corrigée — les pages et leurs pastilles, chaque point expliqué
 *    (`<ActivityWork>`), « La correction t'a aidé ? », « J'ai compris ».
 * Toutes les sorties mènent à l'écran de fin (`onSkip`, `onUnderstood`).
 *
 * **Un seul gabarit** pour toutes les étapes (Wajih, 20/10) : une étape ne remonte plus la page, elle
 * y entre. Les quatre temps de Sam (`intro`, `waiting`, `ready`, `ending`) sont une seule scène,
 * `<ChampionStage>` : sur scène, Sam en grand et ses mots ; à l'attente, il **se range** en glissant
 * jusqu'à l'en-tête, puis le défi entre ; à la correction prête, il revient sur scène. Ses mots
 * changent d'une étape à l'autre par `<StepStage>`, et la copie corrigée prend la place de la scène
 * comme une page (`entree`). Sam garde un seul visage du début à la fin, sa tête V16.
 * En mouvement réduit, chaque étape est posée aussitôt.
 *
 * L'attente se dit en `<WaitingText>` (« Sam corrige ta copie », souligné en boucle), la seule façon
 * d'attendre de l'app ; plus de trois points posés nus hors de leur bulle.
 *
 * En live, la bulle tandem reste au coin pendant toutes les étapes : c'est elle qui dit le mode (Wajih,
 * 20/10). Elle n'est pas à la page : posée une fois au-dessus des étapes (`<FloatingSlot>`), le
 * gabarit lui garde sa place.
 *
 * L'écran garde ce qui est à lui : le relevé de la correction toutes les 5 s (18 fois), le temps
 * de l'intro et de l'annonce, le tirage du défi ou de la pause.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { type UIActivityWork } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { StepStage } from "../../atoms/StepStage"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { WaitingText } from "../../atoms/WaitingText"
import { Card } from "../../molecules/Card"
import { ChampionStage } from "../../molecules/ChampionStage"
import { ConversationHeader } from "../../molecules/ConversationHeader"
import { CorrectionFeedback, type UICorrectionFeedback } from "../../molecules/CorrectionFeedback"
import { CorrectionMoment } from "../../molecules/CorrectionMoment"
import { ActivityWork } from "../../organisms/ActivityWork"
import { PageTemplate } from "../../templates/PageTemplate"

export type StudentCorrectionStep = "intro" | "waiting" | "ready" | "ending" | "copy"

export type StudentCorrectionPageProps = {
    step: StudentCorrectionStep
    /** `waiting` : de quoi patienter — `<CorrectionChallenge>` ou `<CorrectionPause>`. */
    game?: React.ReactNode
    /** `ending` : ce qui dit pourquoi la correction ne viendra pas. */
    endingText?: string
    /** « Continuer sans attendre » (`waiting`), « Activité suivante » (`ending`). */
    onSkip: () => void
    /** `copy` : la copie corrigée, ses pages et ses points. */
    work?: UIActivityWork | null
    /** `copy` : l'avis sur la correction. */
    feedback?: UICorrectionFeedback | null
    onFeedback?: (value: UICorrectionFeedback) => void
    feedbackSubmitting?: boolean
    /** L'avis n'a pas pu partir : ce qu'on en dit, sous les pouces. Absent : rien. */
    feedbackError?: string | null
    /** `copy` : « J'ai compris » ; la croix de l'en-tête mène au même endroit. */
    onUnderstood?: () => void
    testID?: string
}

/** La colonne de l'attente, celle de l'ancien écran : un téléphone, même sur un grand écran. */
const WAIT_WIDTH = 480
/** La colonne de la copie corrigée : une page lisible, ses points à côté au large. */
const COPY_WIDTH = 760

const TEXTS = {
    correcting: "Sam corrige ta copie",
    arriving: "Ta correction arrive dans un instant.",
    meanwhile: "En attendant ta correction",
    skip: "Continuer sans attendre",
    ready: "Ta correction est prête !",
    next: "Activité suivante",
    copyTitle: "Mon rendu",
    understood: "J'ai compris",
    close: "Fermer",
}

export function StudentCorrectionPage(props: StudentCorrectionPageProps) {
    const { step, onSkip, onUnderstood, testID } = props
    const copy = step === "copy"

    // Un seul gabarit : seuls l'en-tête et le pied suivent l'étape. La colonne garde la largeur de la
    // copie, l'attente se tient dans la sienne : rien ne s'élargit sous l'étape qui sort.
    return (
        <PageTemplate
            header={
                copy ? (
                    <ConversationHeader
                        name={TEXTS.copyTitle}
                        maxContentWidth={COPY_WIDTH}
                        end={
                            <IconButton
                                iconName="times"
                                appearance="transparent"
                                iconColor="primary"
                                size={44}
                                iconSize={20}
                                onPress={onUnderstood}
                                accessibilityLabel={TEXTS.close}
                            />
                        }
                    />
                ) : undefined
            }
            scroll={false}
            maxContentWidth={COPY_WIDTH}
            padding="4"
            footer={
                copy ? (
                    <Button label={TEXTS.understood} onPress={onUnderstood} fullWidth />
                ) : step === "waiting" ? (
                    <View style={styles.center}>
                        <TextButton label={TEXTS.skip} color="text-muted" onPress={onSkip} />
                    </View>
                ) : undefined
            }
            testID={testID}
        >
            <StepStage stepKey={copy ? "copy" : "sam"} enter="entree" fill>
                {copy ? <CopyStep {...props} /> : <SamStep {...props} />}
            </StepStage>
        </PageTemplate>
    )
}

/** Les quatre temps de Sam : une seule scène, d'où il se range et où il revient. */
function SamStep(props: StudentCorrectionPageProps) {
    const { step, game, endingText, onSkip } = props
    return (
        <View style={styles.wait}>
            <ChampionStage
                name="sam"
                docked={step === "waiting"}
                stage={
                    <StepStage stepKey={step}>
                        {step === "ready" ? (
                            <CorrectionMoment title={TEXTS.ready} tone="success" champion={false} />
                        ) : step === "ending" ? (
                            <CorrectionMoment title={endingText ?? ""} champion={false}>
                                <Button label={TEXTS.next} onPress={onSkip} />
                            </CorrectionMoment>
                        ) : (
                            <CorrectionMoment
                                title={TEXTS.correcting}
                                detail={TEXTS.arriving}
                                working
                                champion={false}
                            />
                        )}
                    </StepStage>
                }
                dock={
                    <WaitingText size="text-lg" weight="bold" color="text-strong" align="start">
                        {TEXTS.correcting}
                    </WaitingText>
                }
            >
                <ScrollView style={styles.fill} showsVerticalScrollIndicator={false}>
                    <Stack gap="2">
                        <SectionLabel>{TEXTS.meanwhile}</SectionLabel>
                        <Card p="5">{game}</Card>
                    </Stack>
                </ScrollView>
            </ChampionStage>
        </View>
    )
}

/** La copie corrigée : les pages et leurs points, puis l'avis. */
function CopyStep(props: StudentCorrectionPageProps) {
    const { work, feedback, onFeedback, feedbackSubmitting, feedbackError } = props
    return (
        <ScrollView style={styles.fill} contentContainerStyle={styles.copy} showsVerticalScrollIndicator={false}>
            <Stack gap="5">
                {work != null && <ActivityWork work={work} />}
                {onFeedback != null && (
                    <CorrectionFeedback
                        value={feedback ?? null}
                        onChange={onFeedback}
                        submitting={feedbackSubmitting}
                        error={feedbackError}
                    />
                )}
            </Stack>
        </ScrollView>
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: { flex: 1 },
    center: { alignItems: "center" },
    /** La colonne de l'attente, au milieu de celle de la copie. */
    wait: { flex: 1, width: "100%", maxWidth: WAIT_WIDTH, alignSelf: "center" },
    copy: { paddingBottom: theme.spacing["4"] },
}))
